import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '../providers/AuthProvider';
import { useAuth } from '../providers/AuthProvider';
import { SystemSettingsProvider } from '../providers/SystemSettingsProvider';
import ProtectedRoute from '../../components/ProtectedRoute';
import Login from '../../components/Login';
import App from '../AppShell';
// Phase 2 — dev-only scratch page previewing every shared UI primitive.
// Lazy-required so the page only lands in the bundle when NODE_ENV says
// we're in development. CRA's DefinePlugin will inline NODE_ENV and
// tree-shake the import site in production builds.
const IS_DEV = process.env.NODE_ENV === 'development';
let DevUIPage = null;
if (IS_DEV) {
  // eslint-disable-next-line global-require
  DevUIPage = require('../dev/DevUIPage').default;
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
});

/** Handles /auth/callback?accessToken=...&user=... from backend OAuth redirect */
function OAuthCallbackPage() {
  const { completeOAuthLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    async function finishOAuthLogin() {
      const params = new URLSearchParams(window.location.search);
      const error = params.get('error');

      if (error) {
        navigate(`/login?error=${encodeURIComponent(decodeURIComponent(error))}`, { replace: true });
        return;
      }

      try {
        await completeOAuthLogin();
        if (!cancelled) {
          navigate('/', { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          navigate('/login?error=Authentication%20failed', { replace: true });
        }
      }
    }

    finishOAuthLogin();
    return () => {
      cancelled = true;
    };
  }, [completeOAuthLogin, navigate]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#04091a', color: '#8fa4c8', flexDirection: 'column', gap: 16 }}>
      <div style={{ width: 32, height: 32, border: '3px solid #14213d', borderTopColor: '#3b7ffa', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
      <div style={{ fontSize: 13 }}>Signing you in…</div>
    </div>
  );
}

function AppRouter() {
  return (
    <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <AuthProvider>
        <SystemSettingsProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<OAuthCallbackPage />} />
            {IS_DEV && DevUIPage && (
              <Route path="/dev/ui" element={<DevUIPage />} />
            )}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <App />
                </ProtectedRoute>
              }
            />
          </Routes>
        </SystemSettingsProvider>
      </AuthProvider>
    </BrowserRouter>
    </QueryClientProvider>
  );
}

export default AppRouter;
